
import React, { useState } from 'react';
import { Job } from "@/components/jobs/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Edit3, Save, X, Building, MapPin, DollarSign } from 'lucide-react';
import { useUpdateJob } from '@/hooks/mutations/useUpdateJob';
import { toast } from 'sonner';

interface EditableJobHeaderProps {
  job: Job;
  canEdit: boolean;
}

export const EditableJobHeader: React.FC<EditableJobHeaderProps> = ({ job, canEdit }) => {
  const { mutate: updateJob, isPending: isUpdating } = useUpdateJob();
  const [isEditing, setIsEditing] = useState(false);
  
  // Editing states
  const [editTitle, setEditTitle] = useState(job.title);
  const [editCompany, setEditCompany] = useState(job.company || '');
  const [editLocation, setEditLocation] = useState(job.location || '');
  const [editSalaryMin, setEditSalaryMin] = useState(job.salary_min?.toString() || '');
  const [editSalaryMax, setEditSalaryMax] = useState(job.salary_max?.toString() || '');
  const [editStatus, setEditStatus] = useState<Job['status']>(job.status || 'open');

  const handleSave = async () => {
    const updates: Partial<Job> = {
      title: editTitle,
      company: editCompany || null,
      location: editLocation || null,
      salary_min: editSalaryMin ? Number(editSalaryMin) : null,
      salary_max: editSalaryMax ? Number(editSalaryMax) : null,
      status: editStatus,
    };

    updateJob({ id: job.id, data: updates }, {
      onSuccess: () => {
        toast.success('Job details updated successfully');
        setIsEditing(false);
      },
      onError: () => {
        toast.error('Failed to update job details');
      }
    });
  };

  const handleCancel = () => {
    setEditTitle(job.title);
    setEditCompany(job.company || '');
    setEditLocation(job.location || '');
    setEditSalaryMin(job.salary_min?.toString() || '');
    setEditSalaryMax(job.salary_max?.toString() || '');
    setEditStatus(job.status || 'open');
    setIsEditing(false);
  };

  const handleStatusChange = (value: string) => {
    setEditStatus(value as Job['status']);
  };

  const getStatusColor = (status?: Job['status']) => {
    switch (status) {
      case 'open': return 'bg-green-100 text-green-800 border-green-300';
      case 'filled': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'on-hold': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'archived': return 'bg-gray-200 text-gray-700 border-gray-400';
      case 'closed': return 'bg-red-100 text-red-800 border-red-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  if (isEditing) {
    return (
      <Card className="border-l-4 border-l-primary">
        <CardHeader>
          <CardTitle>Edit Job Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Job Title *</label>
              <Input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="Enter job title..."
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Company</label>
              <Input
                value={editCompany}
                onChange={(e) => setEditCompany(e.target.value)}
                placeholder="Enter company name..."
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Location</label>
              <Input
                value={editLocation}
                onChange={(e) => setEditLocation(e.target.value)}
                placeholder="Enter location..."
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Status</label>
              <Select value={editStatus} onValueChange={handleStatusChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="on-hold">On Hold</SelectItem>
                  <SelectItem value="filled">Filled</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Salary Min</label>
              <Input
                type="number"
                value={editSalaryMin}
                onChange={(e) => setEditSalaryMin(e.target.value)}
                placeholder="Minimum salary..."
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Salary Max</label>
              <Input
                type="number"
                value={editSalaryMax}
                onChange={(e) => setEditSalaryMax(e.target.value)}
                placeholder="Maximum salary..."
              />
            </div>
          </div>
          <div className="flex gap-2 pt-4">
            <Button onClick={handleSave} disabled={isUpdating || !editTitle.trim()}>
              <Save className="h-4 w-4 mr-2" />
              {isUpdating ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button variant="outline" onClick={handleCancel} disabled={isUpdating}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-l-4 border-l-primary">
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-grow">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-foreground">{job.title}</h1>
              <div className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(job.status)}`}>
                {job.status ? job.status.charAt(0).toUpperCase() + job.status.slice(1).replace('-', ' ') : 'Open'}
              </div>
            </div>
            <div className="flex items-center gap-4 text-muted-foreground mb-4">
              {job.company && (
                <div className="flex items-center gap-1">
                  <Building className="h-4 w-4" />
                  <span className="font-medium">{job.company}</span>
                </div>
              )}
              {job.location && (
                <div className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  <span>{job.location}</span>
                </div>
              )}
              {(job.salary_min || job.salary_max) && (
                <div className="flex items-center gap-1">
                  <DollarSign className="h-4 w-4" />
                  <span>
                    {job.salary_min && job.salary_max 
                      ? `$${job.salary_min.toLocaleString()} - $${job.salary_max.toLocaleString()}`
                      : job.salary_min 
                        ? `From $${job.salary_min.toLocaleString()}`
                        : `Up to $${job.salary_max?.toLocaleString()}`
                    }
                  </span>
                </div>
              )}
            </div>
          </div>
          {canEdit && (
            <Button variant="outline" onClick={() => setIsEditing(true)}>
              <Edit3 className="h-4 w-4 mr-2" />
              Edit Details
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
